/* eslint-disable max-lines */
/* eslint-disable max-len */
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import EXPIRY_OPTIONS from 'common/constants/tagExpiryOptions';
import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import PatientBanner from 'common/modules/PatientBanner';
import SimplePatientBanner from 'common/modules/PatientNameBanner';
import { addDays, addHours, endOfDay, format, isValid, startOfDay } from 'date-fns';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { listLineItemTags } from 'store/thunks/tagThunks';
import { sortTags } from 'common/utils';
import SYSTEM_TAG_NAMES from 'common/constants/systemTags';

const ManageTagsDialog = ({
  open,
  onClose,
  mpi,
  orderId,
  lineItemId,
  existingTags,
  onSave,
  patientDetails,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const allItemTags = useSelector(({ systemConfigs }) => systemConfigs.lineItemTags || []);
  // Exclude system-managed tags from selectable options — users must not add/remove them
  const systemItemTags = allItemTags.filter((tag) => !SYSTEM_TAG_NAMES.includes(tag));

  const formatTagText = (tag) => {
    const tagName = typeof tag === 'string' ? tag : tag?.tagName || '';
    if (!tagName) return '';
    return tagName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const unFormatTagText = (formattedTag) => {
    return formattedTag
      .split(' ')
      .map((word) => word.toUpperCase())
      .join('_');
  };

  const formattedTagOptions = systemItemTags.map(formatTagText);

  const normalizeExistingTags = (tags) => {
    if (!tags || tags.length === 0) return [];
    return tags.map((tag) => ({
      tagName: tag.tagName || '',
      ...(tag.expiry && { expiry: tag.expiry }),
      ...(tag.createdAt && { createdAt: tag.createdAt }),
    }));
  };

  const existingTagsNormalized = normalizeExistingTags(existingTags);
  const systemExistingTags = existingTagsNormalized.filter(
    (tag) => SYSTEM_TAG_NAMES.includes(tag.tagName) && tag.tagName !== 'BACKFILLED_SHIP_BY'
  );
  const userExistingTags = sortTags(
    existingTagsNormalized.filter((tag) => !SYSTEM_TAG_NAMES.includes(tag.tagName))
  );
  const formattedExistingTags = userExistingTags.map((tag) => formatTagText(tag));

  const [selectedTagsWithExpiry, setSelectedTagsWithExpiry] = useState(
    userExistingTags.map((tag) => ({
      tagName: tag.tagName,
      formattedTagName: formatTagText(tag),
      ...(tag.expiry && { expiry: new Date(tag.expiry) }),
      ...(tag.createdAt && { createdAt: new Date(tag.createdAt) }),
    }))
  );

  const [tagResetCount, setTagResetCount] = useState(0);
  const [expiryPopoverState, setExpiryPopoverState] = useState(null);
  const [selectedExpiryOption, setSelectedExpiryOption] = useState('');
  const [customExpiryDate, setCustomExpiryDate] = useState(null);
  const [urgentCustomDate, setUrgentCustomDate] = useState(new Date());
  const [urgentEverRemovedInSession, setUrgentEverRemovedInSession] = useState(false);

  // Detect if URGENT tag is being newly added or removed in this session
  const wasUrgent = existingTagsNormalized.some((t) => t.tagName === 'URGENT');
  const willBeUrgent = selectedTagsWithExpiry.some((t) => t.tagName === 'URGENT');

  // urgentAdded is true when urgent is selected AND it was either not originally present,
  // or it was removed and re-added during this session
  const urgentAdded = willBeUrgent && (!wasUrgent || urgentEverRemovedInSession);

  useEffect(() => {
    if (open && systemItemTags.length === 0) {
      dispatch(listLineItemTags());
    }
  }, [dispatch, open, systemItemTags.length]);

  // Reset urgent date to today when URGENT is deselected
  useEffect(() => {
    if (!urgentAdded) {
      setUrgentCustomDate(new Date());
    }
  }, [urgentAdded]);

  useEffect(() => {
    if (open) {
      const normalized = normalizeExistingTags(existingTags);
      const sortedUserTags = sortTags(
        normalized.filter((tag) => !SYSTEM_TAG_NAMES.includes(tag.tagName))
      );
      setSelectedTagsWithExpiry(
        sortedUserTags.map((tag) => ({
          tagName: tag.tagName,
          formattedTagName: formatTagText(tag),
          ...(tag.expiry && { expiry: new Date(tag.expiry) }),
          ...(tag.createdAt && { createdAt: new Date(tag.createdAt) }),
        }))
      );
      setUrgentEverRemovedInSession(false);
    }
  }, [open, existingTags]);

  const handleClose = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const handleTagSelectionChange = (selectedFormattedTags) => {
    const urgentWasSelected = selectedTagsWithExpiry.some((t) => t.tagName === 'URGENT');
    const selectedTagNames = selectedFormattedTags.map(unFormatTagText);

    const updatedTags = selectedTagNames.map((tagName) => {
      const existing = selectedTagsWithExpiry.find((t) => t.tagName === tagName);
      return (
        existing ?? {
          tagName,
          formattedTagName: formatTagText({ tagName }),
        }
      );
    });

    const urgentWillBeSelected = updatedTags.some((t) => t.tagName === 'URGENT');

    if (urgentWasSelected && !urgentWillBeSelected) {
      setUrgentEverRemovedInSession(true);
    }

    const sortedTags = sortTags(updatedTags);
    setSelectedTagsWithExpiry(sortedTags);
  };

  const handleRemoveTag = (tagName, form) => {
    if (tagName === 'URGENT') {
      setUrgentEverRemovedInSession(true);
    }
    const updatedTags = selectedTagsWithExpiry.filter((tag) => tag.tagName !== tagName);
    setSelectedTagsWithExpiry(updatedTags);

    const updatedFormattedTags = updatedTags.map((tag) => tag.formattedTagName);
    form.change('tags', updatedFormattedTags);
    if (updatedTags.length === 0) {
      setTagResetCount((prev) => prev + 1);
    }
  };

  const getExpiryOptionValue = (expiryDate) => {
    if (!expiryDate) return '';

    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    const tolerance = 1 / 60;
    const matchingOption = EXPIRY_OPTIONS.find((option) => {
      if (!option.hours && !option.days) return false;
      const expectedHours = option.hours ?? option.days * 24;
      return Math.abs(diffHours - expectedHours) < tolerance;
    });

    return matchingOption?.value ?? '';
  };

  const handleExpiryClick = (event, index) => {
    setExpiryPopoverState({ anchor: event.currentTarget, tagIndex: index });
    const currentExpiry = selectedTagsWithExpiry[index]?.expiry;
    setSelectedExpiryOption(getExpiryOptionValue(currentExpiry) || '');
    const optionVal = getExpiryOptionValue(currentExpiry);
    if (currentExpiry && !optionVal) {
      setCustomExpiryDate(currentExpiry);
    } else {
      setCustomExpiryDate(null);
    }
  };

  const handleExpiryClose = () => {
    setExpiryPopoverState(null);
    setSelectedExpiryOption('');
    setCustomExpiryDate(null);
  };

  const handleExpiryDateChange = (date, index) => {
    setCustomExpiryDate(date);
    setSelectedExpiryOption('');
    setSelectedTagsWithExpiry((prev) => {
      const updated = [...prev];
      const expiryAtEndOfDay = date ? endOfDay(date) : null;
      updated[index] = {
        ...updated[index],
        expiry: expiryAtEndOfDay,
      };
      return updated;
    });
  };

  const handleExpiryChange = (optionValue, index) => {
    setSelectedExpiryOption(optionValue);
    setCustomExpiryDate(null);
    setSelectedTagsWithExpiry((prev) => {
      const updated = [...prev];
      if (!optionValue) {
        updated[index] = {
          ...updated[index],
          expiry: null,
        };
        return updated;
      }

      const selectedOption = EXPIRY_OPTIONS.find((opt) => opt.value === optionValue);
      if (!selectedOption) {
        return updated;
      }

      const now = new Date();
      const expiryDate = selectedOption.hours
        ? addHours(now, selectedOption.hours)
        : addDays(now, selectedOption.days);

      updated[index] = {
        ...updated[index],
        expiry: expiryDate,
      };
      return updated;
    });
  };

  const handleResetExpiry = () => {
    setSelectedExpiryOption('');
    setCustomExpiryDate(null);
    if (expiryPopoverState?.tagIndex != null) {
      handleExpiryChange('', expiryPopoverState.tagIndex);
    }
  };

  const handleSaveTags = () => {
    // Merge system tags back — they are never in selectedTagsWithExpiry but must be preserved
    const systemTagsToPreserve = systemExistingTags.map((tag) => ({
      tagName: tag.tagName,
      ...(tag.expiry && { expiry: new Date(tag.expiry).toISOString() }),
    }));
    const tagsToSave = [
      ...systemTagsToPreserve,
      ...selectedTagsWithExpiry.map((tag) => ({
        tagName: tag.tagName,
        ...(tag.expiry && { expiry: tag.expiry.toISOString() }),
      })),
    ];

    // When URGENT is newly added, pass the selected ship-by date
    const urgentShipByDate =
      urgentAdded && isValid(urgentCustomDate) ? format(urgentCustomDate, 'yyyy-MM-dd') : undefined;

    return onSave({
      lineItemId,
      tags: tagsToSave,
      urgentShipByDate,
    }).then((result) => {
      if (result) {
        enqueueSnackbar('Tags updated successfully', { variant: 'success' });
        onClose();
      } else {
        enqueueSnackbar('Failed to update tags', { variant: 'error' });
      }
    });
  };

  const initialValues = {
    tags: formattedExistingTags,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby='ManageTags-dialog-header'
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle id='ManageTags-dialog-header'>Manage Tags</DialogTitle>

      <DialogContent>
        <Form
          initialValues={initialValues}
          onSubmit={handleSaveTags}
          render={({ handleSubmit, submitting, invalid, form }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container direction='column' spacing={2}>
                <Grid item>
                  {patientDetails ? (
                    <SimplePatientBanner patientName={patientDetails.patientName} />
                  ) : (
                    <PatientBanner mpi={mpi} />
                  )}
                </Grid>

                <Grid item>
                  <Divider />
                </Grid>

                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='subtitle2' fontWeight='bold'>
                      Order ID:
                    </Typography>
                    <Typography variant='body2'>{orderId}</Typography>
                  </Box>
                </Grid>

                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='subtitle2' fontWeight='bold'>
                      Line Item ID:
                    </Typography>
                    <Typography variant='body2'>{lineItemId}</Typography>
                  </Box>
                </Grid>

                <Grid item>
                  <Divider />
                </Grid>

                <Grid item>
                  {selectedTagsWithExpiry.length > 0 && (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 1,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        {/* System tags — read-only, always shown first */}
                        {systemExistingTags.map((tag) => (
                          <Chip
                            key={tag.tagName}
                            label={formatTagText(tag)}
                            size='small'
                            sx={{ opacity: 0.7, cursor: 'default' }}
                          />
                        ))}

                        {/* User tags — editable */}
                        {selectedTagsWithExpiry.map((tag, index) => (
                          <Chip
                            key={tag.tagName}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant='body2'>{tag.formattedTagName}</Typography>
                                {tag.expiry && (
                                  <Typography variant='caption' color='textSecondary'>
                                    ({format(tag.expiry, 'MM/dd/yyyy HH:mm')})
                                  </Typography>
                                )}
                              </Box>
                            }
                            onDelete={() => handleRemoveTag(tag.tagName, form)}
                            deleteIcon={<CloseIcon />}
                            size='small'
                            sx={{ maxWidth: '100%' }}
                            icon={
                              <IconButton
                                size='small'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpiryClick(e, index);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <CalendarTodayIcon fontSize='small' />
                              </IconButton>
                            }
                          />
                        ))}
                      </Box>

                      {selectedTagsWithExpiry.some((tag) => Boolean(tag.expiry)) && (
                        <Typography variant='caption' color='red' sx={{ display: 'block', mb: 2 }}>
                          * Times shown next to a tag indicate its expiry. Once the time elapses,
                          the tag expires automatically.
                        </Typography>
                      )}
                    </>
                  )}

                  <AutoCompleteMultipleSelect
                    key={tagResetCount}
                    name='tags'
                    label='Tags'
                    id='manageTags-tags-field'
                    options={formattedTagOptions}
                    sortOptions
                    freeSolo={false}
                    disabled={submitting}
                    defaultValue={selectedTagsWithExpiry.map((tag) => tag.formattedTagName)}
                    validations={[]}
                    onChange={(_, value) => handleTagSelectionChange(value, form)}
                  />
                  <Typography variant='caption' color='textSecondary' sx={{ mt: 1 }}>
                    Select from available tags
                  </Typography>
                </Grid>

                {/* Urgent ship-by date section — shown only when URGENT is being newly added */}
                {urgentAdded && (
                  <Grid item>
                    <Divider sx={{ mb: 2 }} />
                    <Alert severity='info' sx={{ mb: 2 }}>
                      Adding an urgent tag will update the ship by date to the date you provide
                      below.
                    </Alert>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        value={urgentCustomDate}
                        onChange={(date) => setUrgentCustomDate(date)}
                        disablePast
                        label='Ship by date'
                        renderInput={({
                          inputRef,
                          InputProps: inputComponentProps,
                          inputProps: nativeInputProps,
                          disabled,
                          error,
                          helperText,
                          label,
                        }) => (
                          <TextField
                            inputRef={inputRef}
                            InputProps={{
                              ...inputComponentProps,
                              inputProps: {
                                ...nativeInputProps,
                                'aria-label': 'Urgent ship-by date',
                              },
                            }}
                            disabled={disabled}
                            error={error}
                            helperText={helperText}
                            label={label}
                            size='small'
                            fullWidth
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                )}
              </Grid>

              <Popover
                open={Boolean(expiryPopoverState?.anchor)}
                anchorEl={expiryPopoverState?.anchor}
                onClose={(e, reason) => {
                  if (reason !== 'backdropClick') handleExpiryClose();
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 2, minWidth: 300 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>Set Expiry Date & Time</Typography>
                    <Tooltip title='Reset' arrow placement='left'>
                      <IconButton
                        size='small'
                        onClick={handleResetExpiry}
                        aria-label='Reset expiry'
                      >
                        <RestoreIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {expiryPopoverState?.tagIndex != null && (
                    <>
                      <FormControl fullWidth size='small' sx={{ mt: 1 }}>
                        <InputLabel id='expiry-duration-label'>Expiry Duration</InputLabel>
                        <Select
                          labelId='expiry-duration-label'
                          id='expiry-duration-select'
                          value={selectedExpiryOption}
                          label='Expiry Duration'
                          onChange={(e) =>
                            handleExpiryChange(e.target.value, expiryPopoverState.tagIndex)
                          }
                        >
                          {EXPIRY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography
                        variant='caption'
                        color='textSecondary'
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Or choose a date (from next day — expiry at end of day):
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                          minDate={startOfDay(addDays(new Date(), 1))}
                          value={
                            customExpiryDate ||
                            (selectedTagsWithExpiry[expiryPopoverState?.tagIndex]?.expiry
                              ? startOfDay(
                                  new Date(
                                    selectedTagsWithExpiry[expiryPopoverState.tagIndex].expiry
                                  )
                                )
                              : null)
                          }
                          onChange={(date) =>
                            handleExpiryDateChange(date, expiryPopoverState.tagIndex)
                          }
                          label='Expiry date (end of day)'
                          renderInput={(params) => (
                            <TextField
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...params}
                              size='small'
                              fullWidth
                              sx={{ mt: 1 }}
                              inputProps={{ ...params.inputProps, readOnly: true }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </>
                  )}
                  {expiryPopoverState?.tagIndex != null &&
                    selectedTagsWithExpiry[expiryPopoverState.tagIndex]?.expiry && (
                      <Typography
                        variant='caption'
                        color='textSecondary'
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Expires:{' '}
                        {format(
                          selectedTagsWithExpiry[expiryPopoverState.tagIndex].expiry,
                          'MM/dd/yyyy HH:mm'
                        )}
                      </Typography>
                    )}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size='small' variant='outlined' onClick={handleExpiryClose}>
                      Close
                    </Button>
                    <Button
                      size='small'
                      variant='contained'
                      color='primary'
                      onClick={handleExpiryClose}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Popover>

              <DialogActions sx={{ mt: 3, px: 0 }}>
                <Button
                  onClick={handleClose}
                  variant='outlined'
                  color='secondary'
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={submitting}
                  disabled={submitting || invalid || (urgentAdded && !isValid(urgentCustomDate))}
                  type='submit'
                >
                  Save Tags
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

ManageTagsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  lineItemId: PropTypes.string.isRequired,
  existingTags: PropTypes.arrayOf(
    PropTypes.shape({
      tagName: PropTypes.string.isRequired,
      expiry: PropTypes.string,
      createdAt: PropTypes.string,
    })
  ),
  onSave: PropTypes.func.isRequired,
  patientDetails: PropTypes.shape({
    patientName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dob: PropTypes.string,
    email: PropTypes.string,
    status: PropTypes.string,
  }),
};

ManageTagsDialog.defaultProps = {
  existingTags: [],
  patientDetails: null,
};

export default ManageTagsDialog;
